/* eslint-disable jsx-a11y/anchor-is-valid */

import { CollapsiblePanel, Panel } from "../../../widgets";
import { useEffect, useRef, useState } from "react";

import { AvatarText } from "../avatar/AvatarText";
import { FieldFilterType } from "../../../base/types";
import { IChatMessage } from "../../../data";
import { ITableLookup } from "../../../base/models";
import clsx from "clsx";
import moment from "moment";
import { useAuth } from "../../../../modules/auth";
import { useLazyReadMultipleQuery } from "../../../services/data/tableDataApi";
import { useParams } from "react-router-dom";

export const Chat = ({ title, icon, messages, headerClass, collapsible, className, onSendMessage, contactId, tableLookup, recordId }: {
    className?: string,
    title?: string,
    icon?: string,
    messages: IChatMessage[],
    headerClass?: string,
    collapsible?: boolean,
    tableLookup: ITableLookup,
    recordId?: string
    onSendMessage: (message: Partial<IChatMessage>) => void,
    contactId?: string,
}) => {
    if (collapsible) {
        return (
            <CollapsiblePanel
                eventKey="chat-component"
                className={className}
                header={title}
                icon={icon}
                headerClass={headerClass}
            >
                <ChatBody messages={messages} onSendMessage={onSendMessage} contactId={contactId || ""} tableLookup={tableLookup} recordId={recordId} />
            </CollapsiblePanel>
        );
    }

    return (
        <Panel
            className={className}
            header={title}
            icon={icon}
            headerClass={headerClass}>
            <ChatBody messages={messages} onSendMessage={onSendMessage} contactId={contactId || ""} tableLookup={tableLookup} recordId={recordId} />
        </Panel>);
};

const ChatBody = ({ messages, onSendMessage, className, contactId, tableLookup, recordId }: {
    messages: IChatMessage[],
    className?: string,
    tableLookup: ITableLookup,
    recordId?: string
    onSendMessage: (message: Partial<IChatMessage>) => void,
    contactId: string,
}) => {
    const [message, setMessage] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [errMsg, setErrMsg] = useState<string>();
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const sendMessage = async () => {
        setLoading(true);
        setErrMsg('');

        try {
            await onSendMessage({
                message,
                messageType: 365970000,
            });
            setMessage('');
        } catch (err) {
            setErrMsg(`Couldn't send the message`);
        }

        setLoading(false);
    };

    const onEnterPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.keyCode === 13 && e.shiftKey === false) {
            e.preventDefault();
            sendMessage();
        }
    };

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    return (<>
        <div className={clsx(className)}>
            <div className={clsx('scroll-y me-n5 pe-5 mh-200px')}>
                {messages.slice().sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime()).map((message) => (
                    <ChatMessage
                        message={message}
                        key={message.id}
                        contactId={contactId}
                        tableLookup={tableLookup}
                        recordId={recordId} />
                ))}
                <div ref={messagesEndRef} />
            </div>

            <div className='card-footer pt-4 pb-0 border-0 px-0'>
                <div className='d-flex flex-stack'>
                    <textarea
                        className='form-control form-control-flush mb-3 me-3'
                        rows={1}
                        data-kt-element='input'
                        placeholder='Type a comment'
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        onKeyDown={onEnterPress}
                    ></textarea>
                    <button
                        className='btn btn-primary'
                        type='button'
                        disabled={loading || !message.trim()}
                        data-kt-element='send'
                        onClick={sendMessage}
                    >
                        {!loading && <span className='indicator-label'>Send</span>}
                        {loading && (
                            <span className='indicator-progress' style={{ display: 'block' }}>
                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                            </span>
                        )}
                    </button>
                </div>
            </div>
        </div>

        {errMsg && <div className="alert alert-warning mt-5">{errMsg}</div>}
    </>);
};

const ChatMessage = ({ message, contactId, tableLookup, recordId }: {
    message: IChatMessage,
    contactId: string,
    tableLookup: ITableLookup
    recordId?: string
}) => {
    const messageType = message.userName !== contactId ? 'in' : 'out';
    const state = messageType === 'in' ? 'info' : 'primary';
    const contentClass = `d-flex justify-content-${messageType === 'in' ? 'start' : 'end'} mb-2`;
    const { tableId, idField, displayField } = tableLookup;
    const { user } = useAuth();
    const { siteId } = useParams()
    const [readRecords, { data }] = useLazyReadMultipleQuery()
    const [name, setName] = useState<string>()

    useEffect(() => {
        if (!recordId || !tableId || !idField || !displayField || !siteId)
            return;

        const fieldList = [idField, displayField]
        readRecords({
            userId: user?.userId || '',
            portalId: siteId,
            tableId,
            fieldList,
            recordLimit: 1,
            filters: [{ group: 0, filters: tableLookup.filters || [] },
            { group: 1, filters: [{ field: idField, type: FieldFilterType.Const, value: recordId }] }],
        })

        // eslint-disable-next-line
    }, [user, tableLookup, recordId])

    useEffect(() => {
        if (!data) return;

        const record = data.records.find(p => p[idField!] === recordId)
        if (record)
            setName(record[displayField!] || '');
        // eslint-disable-next-line
    }, [data])

    return (
        <div className={clsx('d-flex', contentClass, 'mb-2')}>
            <div
                className={clsx(
                    'd-flex flex-column align-items',
                    `align-items-${messageType === 'in' ? 'start' : 'end'}`
                )}
            >
                <div className='d-flex align-items-center mb-2'>
                    <UserInfo message={message} contactId={contactId} name={name || ""} />
                </div>

                <div
                    className={clsx(
                        'p-2 px-5 rounded',
                        `bg-light-${state}`,
                        'text-gray-700 fw-bold ',
                        `text-${messageType === 'in' ? 'start' : 'end'}`
                    )}
                    dangerouslySetInnerHTML={{ __html: message.message || '' }}
                />
            </div>
        </div>
    );
};

const UserInfo = ({ message, contactId, name }: { message: IChatMessage, contactId: string, name: string }) => {
    const messageType = message.userName !== contactId ? 'in' : 'out';
    switch (messageType) {
        case 'in':
            return (
                <>
                    <AvatarText random={true} label={name} />
                    <div className='ms-2'>
                        <a
                            href='#'
                            className='fs-6 fw-bolder text-gray-700 text-hover-primary me-2'
                        >
                            {message.userName !== 'portal-user' ? name : 'You'}
                        </a>
                        <span className='text-muted fs-7 mb-1'>
                            {moment(message.dateTime).fromNow()}
                        </span>
                    </div>
                </>
            );
        case 'out':
            return (
                <>
                    <div className='me-2'>
                        <span className='text-muted fs-7 mb-2'>
                            {moment(message.dateTime).fromNow()}
                        </span>
                        <a
                            href='#'
                            className='fs-6 fw-bolder text-gray-700 text-hover-primary ms-2'
                        >
                            {message.userName !== 'portal-user' ? name : 'You'}
                        </a>
                    </div>
                    <AvatarText random={true} label={name} />
                </>
            );
    }
};
